// src/components/PhoneVerificationGate.tsx
// ============================================================================
// FILE ROLE: Phone verification gate (server-side OTP flow).
// - Sends phone via AppSync requestPhoneOtp mutation; server generates,
//   hashes, stores, and SMS-delivers the OTP. Client never sees the OTP.
// - Submits the user's entered OTP via AppSync verifyContactPointOTP mutation;
//   server bcrypt-compares, creates the ContactPoint, sets User.phoneVerified.
// - Zero client-side OTP generation, storage, or comparison.
// ============================================================================

import { useState } from "react";
import { generateClient } from "aws-amplify/data";
import type { Schema } from "../../amplify/data/resource";
import OnboardingProgressBar from "./OnboardingProgressBar";

const client = generateClient<Schema>();

interface PhoneVerificationGateProps {
  onComplete: (phoneE164: string) => Promise<void>;
}

/** Keep only digits (0-9) */
function digitsOnly(s: string) {
  return s.replace(/\D/g, "");
}

/** Format 10 US digits as (XXX) XXX-XXXX while typing */
function formatUSPhone(d: string) {
  const n = digitsOnly(d).slice(0, 10);
  if (n.length < 4) return n;
  if (n.length < 7) return `(${n.slice(0, 3)}) ${n.slice(3, 6)}`;
  return `(${n.slice(0, 3)}) ${n.slice(3, 6)}-${n.slice(6)}`;
}

/** Convert 10 digits to E.164 (+1XXXXXXXXXX) */
function toE164(n10: string) {
  const n = digitsOnly(n10);
  return n.length === 10 ? `+1${n}` : "";
}

/** Format a retry-after ISO timestamp for display */
function formatCooldownMessage(retryAfter: string): string {
  try {
    return `Too many attempts. Please try again after ${new Date(retryAfter).toLocaleTimeString()}.`;
  } catch {
    return "Too many attempts. Please try again later.";
  }
}

type MutationReason =
  | "PHONE_MISMATCH"
  | "COOLDOWN_ACTIVE"
  | "SMS_SEND_FAILED"
  | "USER_NOT_FOUND"
  | "UNAUTHENTICATED"
  | "MISSING_ARGUMENTS"
  | "INVALID_PHONE_FORMAT"
  | "INVALID_CODE"
  | "OTP_EXPIRED"
  | "NO_PENDING_OTP"
  | "PHONE_CLAIMED_BY_OTHER"
  | "INTERNAL_ERROR"
  | string;

interface MutationResult {
  ok: boolean;
  reason?: MutationReason;
  retryAfter?: string;
  // Dev-only: requestPhoneOtp echoes the plaintext OTP back when the backend
  // is running in dev. Strictly absent in prod responses (handler-gated).
  devOtp?: string;
}

const PhoneVerificationGate = ({ onComplete }: PhoneVerificationGateProps) => {
  const [phoneDigits, setPhoneDigits] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [smsConsent, setSmsConsent] = useState(false);
  // True after the user clicks "Use a different number" — flagged so the next
  // requestPhoneOtp call bypasses the PHONE_MISMATCH guard and resets the
  // primary phone server-side.
  const [intendsChange, setIntendsChange] = useState(false);

  const isValidUS = phoneDigits.length === 10;

  const handleSendCode = async () => {
    setError(null);

    if (!isValidUS) {
      setError("Please enter a valid 10-digit US phone number.");
      return;
    }

    if (!smsConsent) {
      setError("Please agree to receive a verification code via SMS.");
      return;
    }

    const e164 = toE164(phoneDigits);
    if (!e164) {
      setError("Failed to normalize phone number.");
      return;
    }

    setLoading(true);
    try {
      const response = await client.mutations.requestPhoneOtp({
        phone: e164,
        forceReset: intendsChange,
      });

      if (response.errors && response.errors.length > 0) {
        void ["[PhoneVerificationGate] requestPhoneOtp errors", response.errors];
        setError("Something went wrong. Please try again.");
        return;
      }

      // AWSJSON arrives as a JSON-encoded string on the wire; parse defensively.
      let parsed: any = response.data;
      if (typeof parsed === 'string') {
        try { parsed = JSON.parse(parsed); } catch { parsed = null; }
      }
      const result = parsed as MutationResult | null;

      if (result?.ok) {
        // Dev-only convenience: auto-fill the OTP input when the backend
        // echoed it back. Never set in prod (handler gates on AMPLIFY_ENV).
        if (result.devOtp) {
          setVerificationCode(result.devOtp);
        }
        setCodeSent(true);
        return;
      }

      switch (result?.reason) {
        case "PHONE_MISMATCH":
          setError("This phone number doesn't match your account.");
          break;
        case "COOLDOWN_ACTIVE":
          setError(result.retryAfter
            ? formatCooldownMessage(result.retryAfter)
            : "Too many attempts. Please try again later.");
          break;
        case "SMS_SEND_FAILED":
          // Brief AD: The pre-SMS write already bootstrapped User.phone to the
          // number that failed to send. Prime intendsChange so the next submit
          // sends forceReset: true and replaces it cleanly — without forcing
          // the user to click "Use a different number" manually.
          setIntendsChange(true);
          setError("We couldn't send the code. Please try again.");
          break;
        case "USER_NOT_FOUND":
          setError("Account not found. Please sign in again.");
          break;
        default:
          setError("Something went wrong. Please try again.");
      }
    } catch (err) {
      void ["[PhoneVerificationGate] requestPhoneOtp threw", err];
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    setError(null);

    if (verificationCode.length !== 6) {
      setError("Please enter the 6-digit code.");
      return;
    }

    const e164 = toE164(phoneDigits);
    if (!e164) {
      setError("Failed to normalize phone number.");
      return;
    }

    setLoading(true);
    try {
      const response = await client.mutations.verifyContactPointOTP({
        phone: e164,
        otpCode: verificationCode,
      });

      if (response.errors && response.errors.length > 0) {
        void ["[PhoneVerificationGate] verifyContactPointOTP errors", response.errors];
        setError("Something went wrong. Please try again.");
        return;
      }

      // AWSJSON arrives as a JSON-encoded string on the wire; parse defensively.
      let parsed: any = response.data;
      if (typeof parsed === 'string') {
        try { parsed = JSON.parse(parsed); } catch { parsed = null; }
      }
      const result = parsed as MutationResult | null;

      if (result?.ok) {
        await onComplete(e164);
        return;
      }

      switch (result?.reason) {
        case "INVALID_CODE":
          setError("Incorrect code. Please try again.");
          break;
        case "OTP_EXPIRED":
          setCodeSent(false);
          setVerificationCode("");
          setError("Your code expired. Please request a new one.");
          break;
        case "NO_PENDING_OTP":
          setCodeSent(false);
          setVerificationCode("");
          setError("No code found. Please request a new one.");
          break;
        case "COOLDOWN_ACTIVE":
          setCodeSent(false);
          setVerificationCode("");
          setError(result.retryAfter
            ? formatCooldownMessage(result.retryAfter)
            : "Too many attempts. Please try again later.");
          break;
        case "PHONE_CLAIMED_BY_OTHER":
          setError("This phone number is already associated with another account.");
          break;
        default:
          setError("Something went wrong. Please try again.");
      }
    } catch (err) {
      void ["[PhoneVerificationGate] verifyContactPointOTP threw", err];
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Brand pill input style — same coral pill used in sign-in / sign-up.
  const pillInputStyle: React.CSSProperties = {
    width: "100%",
    border: "2.5px solid #E05A3A",
    borderRadius: 100,
    padding: "14px 20px",
    fontFamily: "'Fredoka', sans-serif",
    fontSize: 16,
    background: "#FDF6EE",
    color: "#1A1A1A",
    outline: "none",
    boxSizing: "border-box",
    boxShadow: "none",
  };

  // Coral submit pill (active) and disabled variant.
  const submitPill = (disabled: boolean): React.CSSProperties => ({
    width: "100%",
    background: "#E05A3A",
    color: "#FFFFFF",
    border: "3px solid #1A1A1A",
    borderRadius: 100,
    boxShadow: "4px 4px 0px #1A1A1A",
    padding: "14px 36px",
    fontFamily: "'Fredoka', sans-serif",
    fontSize: 18,
    fontWeight: 700,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.5 : 1,
    boxSizing: "border-box",
    transition: "transform 0.12s ease, box-shadow 0.12s ease, background 0.12s ease",
  });

  const labelStyle: React.CSSProperties = {
    display: "block",
    marginBottom: 8,
    fontFamily: "'Fredoka', sans-serif",
    fontSize: 15,
    fontWeight: 600,
    color: "#1A1A1A",
  };

  const helperStyle: React.CSSProperties = {
    fontFamily: "'Nunito', sans-serif",
    fontSize: 13,
    color: "#888",
    marginTop: 8,
    marginBottom: 0,
    paddingLeft: 8,
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        padding: 16,
        background: "#F0DFC8",
        fontFamily: "'Nunito', sans-serif",
        boxSizing: "border-box",
      }}
    >
      <div style={{ width: "100%", paddingTop: 8, marginBottom: 48, maxWidth: 400 }}>
        <OnboardingProgressBar
          currentStep={2}
          steps={["Step 1: Age Verification", "Step 2: Phone Verification", "Step 3: Payment", "Done"]}
        />

        <div
          style={{
            background: "#FFFFFF",
            border: "3px solid #1A1A1A",
            borderRadius: 20,
            boxShadow: "6px 6px 0px #1A1A1A",
            padding: "28px 24px",
            boxSizing: "border-box",
            marginTop: 12,
          }}
        >
          {error && (
            <div
              style={{
                marginBottom: 16,
                borderRadius: 12,
                border: "1.5px solid rgba(196, 75, 46, 0.4)",
                background: "rgba(255, 240, 235, 1)",
                padding: 12,
              }}
            >
              <p
                style={{
                  fontFamily: "'Nunito', sans-serif",
                  fontSize: 13,
                  color: "#C44B2E",
                  margin: 0,
                }}
              >
                {error}
              </p>
            </div>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {!codeSent ? (
              <>
                <div>
                  <label htmlFor="phone" style={labelStyle}>
                    Primary Phone Number
                  </label>

                  <input
                    id="phone"
                    type="tel"
                    inputMode="numeric"
                    autoComplete="phone"
                    placeholder="(555) 123-4567"
                    value={formatUSPhone(phoneDigits)}
                    onFocus={() => setPhoneTouched(true)}
                    onChange={(e) => {
                      const next = digitsOnly(e.target.value).slice(0, 10);
                      setPhoneDigits(next);
                    }}
                    style={{
                      ...pillInputStyle,
                      opacity: loading ? 0.6 : 1,
                      cursor: loading ? "not-allowed" : "text",
                    }}
                    aria-invalid={phoneTouched && !isValidUS}
                    pattern="\(\d{3}\)\s\d{3}-\d{4}"
                    title="Enter a US phone number like (555) 123-4567"
                    disabled={loading}
                  />

                  <p style={helperStyle}>US mobile numbers only</p>
                </div>

                <div style={{ display: "flex", alignItems: "flex-start", gap: 12, paddingBottom: 8 }}>
                  <input
                    id="sms-consent"
                    type="checkbox"
                    checked={smsConsent}
                    onChange={(e) => setSmsConsent(e.target.checked)}
                    className="mt-1 h-4 w-4 flex-shrink-0 cursor-pointer accent-[#E05A3A]"
                    disabled={loading}
                  />
                  <label
                    htmlFor="sms-consent"
                    style={{
                      fontFamily: "'Nunito', sans-serif",
                      fontSize: 13,
                      color: "#555",
                      lineHeight: 1.5,
                    }}
                  >
                    I agree to receive a one-time verification code via text (SMS) from CRUSHHH to verify my phone number. Message and data rates may apply. Up to (1) message per verification attempt. Reply STOP to cancel, HELP for help.
                  </label>
                </div>

                <button
                  onClick={handleSendCode}
                  disabled={loading || !isValidUS || !smsConsent}
                  style={submitPill(loading || !isValidUS || !smsConsent)}
                >
                  {loading ? "Sending..." : "Send Verification Code"}
                </button>
              </>
            ) : (
              <>
                <div>
                  <label htmlFor="code" style={labelStyle}>
                    Enter Verification Code
                  </label>
                  <input
                    id="code"
                    type="text"
                    inputMode="numeric"
                    placeholder="000000"
                    maxLength={6}
                    value={verificationCode}
                    autoComplete="one-time-code"
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ""))}
                    style={{
                      ...pillInputStyle,
                      opacity: loading ? 0.6 : 1,
                      cursor: loading ? "not-allowed" : "text",
                      letterSpacing: "0.4em",
                      textAlign: "center",
                    }}
                    disabled={loading}
                  />
                  <p style={helperStyle}>
                    Code sent to {formatUSPhone(phoneDigits)}
                  </p>
                </div>

                <button
                  onClick={handleVerifyCode}
                  disabled={loading || verificationCode.length !== 6}
                  style={submitPill(loading || verificationCode.length !== 6)}
                >
                  {loading ? "Verifying..." : "Verify Code"}
                </button>

                <button
                  onClick={() => {
                    // Reset back to phone-input step. The flag tells the next
                    // requestPhoneOtp call to forceReset, replacing the primary
                    // number that was bootstrap-written on the prior send.
                    setCodeSent(false);
                    setVerificationCode("");
                    setError(null);
                    setIntendsChange(true);
                  }}
                  disabled={loading}
                  style={{
                    width: "100%",
                    background: "transparent",
                    border: "none",
                    fontFamily: "'Nunito', sans-serif",
                    fontSize: 13,
                    color: "#888",
                    cursor: loading ? "not-allowed" : "pointer",
                    padding: 0,
                    marginTop: 4,
                  }}
                >
                  Use a different number
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhoneVerificationGate;
